import { useQuery } from '@tanstack/react-query';
import type {
  ReportEmployeesPerformanceResponse,
  ReportRevenueResponse,
  ReportTopServicesResponse,
} from '@crm/shared';
import { api } from '../../lib/api';

const BASE = '/reports';

export type DateRange = { from: string; to: string };

export const reportKeys = {
  revenue: (r: DateRange) => ['reports', 'revenue', r] as const,
  topServices: (r: DateRange, limit: number) =>
    ['reports', 'top-services', r, { limit }] as const,
  employees: (r: DateRange) => ['reports', 'employees', r] as const,
};

export function useRevenueReport(range: DateRange) {
  return useQuery({
    queryKey: reportKeys.revenue(range),
    queryFn: async (): Promise<ReportRevenueResponse> => {
      const res = await api.get<ReportRevenueResponse>(`${BASE}/revenue`, {
        params: range,
      });
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useTopServicesReport(range: DateRange, limit = 3) {
  return useQuery({
    queryKey: reportKeys.topServices(range, limit),
    queryFn: async (): Promise<ReportTopServicesResponse> => {
      const res = await api.get<ReportTopServicesResponse>(`${BASE}/top-services`, {
        params: { ...range, limit },
      });
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useEmployeesPerformanceReport(range: DateRange) {
  return useQuery({
    queryKey: reportKeys.employees(range),
    queryFn: async (): Promise<ReportEmployeesPerformanceResponse> => {
      const res = await api.get<ReportEmployeesPerformanceResponse>(
        `${BASE}/employees-performance`,
        { params: range },
      );
      return res.data;
    },
    staleTime: 60_000,
  });
}

/**
 * Pobiera CSV jako blob i otwiera download w przeglądarce. Używamy
 * `responseType: 'blob'` zamiast text, żeby BOM (UTF-8 sygnatura)
 * nie został zinterpretowany jako string.
 */
export async function downloadAppointmentsCsv(range: DateRange): Promise<void> {
  const res = await api.get<Blob>(`${BASE}/export`, {
    params: { ...range, type: 'appointments' },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `wizyty_${range.from}_${range.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
